import {
    Button,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    ThemeProvider,
    Typography,
    createTheme
} from "@mui/material";
import React, { useState, useEffect, Fragment } from "react";
import { PageHeader } from "../../components/PageHeader";
import { useStyles } from "../../common/appStyles";
import { ICreateRegionRequest, IRegionDetail, IRegionPaginationResponse, ITenantDetail, IUpdateRegionRequest } from "../../types/regions";
import { RootState } from "../../store/store";
import { useSelector } from "react-redux";
import { PageEnum, Predicates } from "../../types/enums";
import EditIcon from '@mui/icons-material/Edit';
import { showSnackbar } from "../../services/snackbar.service";
import { RegionDetail } from "./RegionDetail";
import { CreateRegion, GetRegionListExpressionBuilder, GetTenantList, UpdateRegion } from "../../services/user-service";
import { IExpressionRequest } from "../../types/expression";
import { RedirectToDash } from "../RedirectToDash";
import { paginationExpression } from "../../utilities/ProtoExpression";

/**
 * @returns html component with a table of all regions and
 *  allows the user to sort them by tenant.
 * @remarks Allows users to view all {@link IRegionDetail | Regions} , sort by a {@link ITenantDetail | tenant} and have buttons to 
 * create new region and edit existing ones through {@link RegionDetail}.
 */
export function RegionAdmin() {
    const classes = useStyles();
    const userRole = useSelector((state: RootState) => state.roles);
    const currentPage = userRole.userPageAccess[PageEnum.RegionAdmin];
    const [showRegionDialog, setShowRegionDialog] = useState(false);
    const [selectedRegion, setSelectedRegion] = React.useState<IRegionDetail>();
    const [pageTitle, setPageTitle] = useState("Region Admin");
    const [titleText, setTitleText] = useState("");
    const [dropDown, setDropDown] = useState(`{"name":"All","id":0}`);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [noResult, setNoResult] = useState(false);
    const [result, setResult] = useState<IRegionPaginationResponse | undefined>(undefined);
    const [tenants, setTenants] = useState<ITenantDetail[]>([]);

    const theme = createTheme({
        palette: {
            primary: {
                main: '#484A57'
            },
            secondary: {
                main: '#10AD61'
            },
            background: {
                default: '#ffffff',
            },
        },
        typography: {
            h6: {
                color: '#484A57'
            },
            h5: {
                color: '#10AD61'
            },
            body1: {
                color: 'black'
            }
        },
    });

    const getRegionList = (expression: IExpressionRequest) => {
        GetRegionListExpressionBuilder(expression)
            .then(response => {
                checkNoFilteredResult(response.regionInformation);
                setResult(response);
            })
            .catch((error) => {
                console.error(error);
            });
    }

    const getTenantList = () => {
        GetTenantList()
            .then(response => {
                setTenants(response);
            })
            .catch((error) => {
                console.error(error);
            });
    }

    const onEditRegion = (row: IRegionDetail) => {
        setTitleText("Edit Region");
        setSelectedRegion(row);
        setShowRegionDialog(true);
    }

    const onAddNewRegion = () => {
        setTitleText("Add Region");
        setSelectedRegion({
            id: 0,
            name: "",
            tenantId: 0,
        });
        setShowRegionDialog(true);
    }

    const handleClose = () => {
        setShowRegionDialog(false);
    }

    const handleSave = async (savedRegion: IUpdateRegionRequest) => {
        try {
            if (savedRegion.id === 0) {
                const region: ICreateRegionRequest = {
                    "regionName": savedRegion["name"],
                    "tenantId": savedRegion["tenantId"]
                };
                await CreateRegion(region);
            }
            else {
                await UpdateRegion(savedRegion);
            }
            showSnackbar('Saved Successfully', 'success', 5000);
            setShowRegionDialog(false);
            let option: ITenantDetail = JSON.parse(dropDown);
            let expression: IExpressionRequest;
            if (savedRegion.id === 0) {
                setPage(0);
                if (option.id === 0) {
                    expression = paginationExpression("Name", "", Predicates.StartsWith, rowsPerPage, 0);
                    getRegionList(expression);
                }
                else {
                    expression = paginationExpression("TenantId", option.id, Predicates.Equals, rowsPerPage, 0);
                    getRegionList(expression);
                }
            }
            else {
                if (option.id === 0) {
                    expression = paginationExpression("Name", "", Predicates.StartsWith, rowsPerPage, page);
                    getRegionList(expression);
                }
                else {
                    expression = paginationExpression("TenantId", option.id, Predicates.Equals, rowsPerPage, page);
                    getRegionList(expression);
                }
            }
        } catch (error: any) {
            showSnackbar(error, 'error', 5000);
        }
    }

    const handleSearch = (input: string) => {
        const option: ITenantDetail = JSON.parse(input);
        setPage(0);
        if (option.id === 0) {
            const expression: IExpressionRequest = paginationExpression("Name", "", Predicates.StartsWith, rowsPerPage, 0);
            getRegionList(expression);
        }
        else {
            const expression: IExpressionRequest = paginationExpression("TenantId", option.id, Predicates.Equals, rowsPerPage, 0);
            getRegionList(expression);
        }
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
        let option: ITenantDetail = JSON.parse(dropDown);
        let expression: IExpressionRequest = paginationExpression("TenantId", option.id, Predicates.Equals, rowsPerPage, newPage);
        if (option.id !== 0) {
            getRegionList(expression);
        }
        else {
            expression = paginationExpression("Name", "", Predicates.StartsWith, rowsPerPage, newPage);
            getRegionList(expression);
        }
    }

    const handleChangeRowsPerPage = (value) => {
        setRowsPerPage(+value);
        setPage(0);
        let option: ITenantDetail = JSON.parse(dropDown);
        let expression: IExpressionRequest = paginationExpression("TenantId", option.id, Predicates.Equals, value, 0);
        if (option.id === 0) {
            expression = paginationExpression("Name", "", Predicates.StartsWith, value, 0);
        }
        getRegionList(expression);
    }

    const checkNoFilteredResult = (check: IRegionDetail[]) => {
        setNoResult(check.length === 0);
    }

    useEffect(() => {
        const expression: IExpressionRequest = paginationExpression("Name", "", Predicates.StartsWith, rowsPerPage, 0);
        getRegionList(expression);
        getTenantList();
    }, []);

    return (
        <Fragment>
            <ThemeProvider theme={theme}>
                {currentPage?.read && <Grid
                    container
                    className={classes.formContainer}
                    spacing={1}
                    justifyContent="center"
                    alignItems={'center'}
                >
                    <Grid item sm={12} className='justify-center' padding="5px">
                        <Grid
                            container
                            spacing={1}
                            direction={{ xs: 'column', md: 'row' }}
                            justifyContent="center"
                            alignItems="center"
                            className={classes.fixGridMargin}
                        >
                            <Grid item xs={2} md={3}><PageHeader title={pageTitle} onBackClick={undefined} /></Grid>
                            <Grid item xs={12} container >
                                <Grid container justifyContent={"center"} alignItems={"center"} spacing={2} direction={{ xs: 'column', md: 'row' }} >
                                    <Grid item xs={2} md={3} lg={2.6} xl={2}>
                                        <Button variant="contained" size="large" fullWidth aria-labelledby="add new region" onClick={() => onAddNewRegion()}>
                                            Add New Region
                                        </Button>
                                    </Grid>
                                    <Grid item xs={1.5}  >
                                        <FormControl fullWidth size="medium">
                                            <InputLabel>Tenant</InputLabel>
                                            <Select
                                                label="Tenant"
                                                value={dropDown}
                                                aria-labelledby="tenant"
                                                MenuProps={{
                                                    disableScrollLock: true,
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: "20vh",
                                                        },
                                                    }
                                                }}
                                                onChange={(values) => {
                                                    setDropDown(values.target.value);
                                                    handleSearch(values.target.value);
                                                }
                                                }
                                            >
                                                <MenuItem value={`{"name":"All","id":0}`}>All</MenuItem>
                                                {tenants.map((tenant) => (
                                                    <MenuItem key={tenant.id} value={JSON.stringify(tenant)} aria-labelledby={tenant.name}>{tenant.name}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid
                        container
                        justifyContent={'center'}
                        alignItems={"center"}
                        spacing={1}
                        direction={"column"}
                        className={classes.gridPaddingTop}
                    >
                        <Grid item sm={11} className={classes.tableGrid}>
                            {(result !== undefined && currentPage) ? (
                                (noResult) || <Paper elevation={3}>
                                    <TableContainer className={classes.tableContainer}>
                                        <Table stickyHeader size="small" aria-label="region table">
                                            <TableHead>
                                                <TableRow className={classes.tableHead}>
                                                    <TableCell key="regionName" aria-labelledby="Region">Region</TableCell>
                                                    {currentPage?.update && <TableCell key="actions" align="right" aria-labelledby="Actions">Actions</TableCell>}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody className={classes.tableBody}>
                                                {result.regionInformation.map((row) => (
                                                    <TableRow
                                                        key={row.id}
                                                        className={classes.table}
                                                    >
                                                        <TableCell align="left" aria-labelledby={row.name}>{row.name}</TableCell>
                                                        <TableCell key="actions" align="right">
                                                            {currentPage?.update &&
                                                                <Button
                                                                    onClick={e => onEditRegion(row)}
                                                                    className="app-button ml10"
                                                                    color='primary'
                                                                    size="small"
                                                                    variant='outlined'
                                                                    aria-labelledby={"edit"} >
                                                                    <EditIcon className="fs20 mr6 icon" /> Edit Region
                                                                </Button>
                                                            }
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <TablePagination
                                        rowsPerPageOptions={[10, 25, 50]}
                                        component="div"
                                        count={result.count}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        onPageChange={handleChangePage}
                                        onRowsPerPageChange={(event) => handleChangeRowsPerPage(event.target.value)}
                                    />
                                </Paper>)
                                :
                                <Grid container direction={"column"} rowSpacing={.5}>
                                    <Grid item xs={12}>
                                        <Skeleton variant='rectangular' width={"100%"} height={"3vh"} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Skeleton variant='rounded' width={'100%'} height={'54vh'} />
                                    </Grid>
                                </Grid>
                            }
                        </Grid>
                        <Grid alignSelf={"center"} className={classes.tableNoResult}>
                            {noResult && <FormControl fullWidth><Typography variant="h5">No Results Found</Typography></FormControl>}
                        </Grid>
                    </Grid>
                    {currentPage?.read && <Grid item sm={12}>
                        {showRegionDialog && <RegionDetail
                            showDialog={showRegionDialog}
                            selectedRegion={selectedRegion}
                            titleText={titleText}
                            handleClose={handleClose}
                            handleSave={handleSave}
                        />}
                    </Grid>}
                </Grid>
                }
                {!currentPage?.read && currentPage?.read !== undefined &&
                    <RedirectToDash />
                }
            </ThemeProvider>
        </Fragment>
    );
}
